import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Form,
  Popconfirm,
  Row,
  theme,
  message,
  Space,
  Skeleton,
  Input,
  Table,
  Select,
  DatePicker
} from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useInvoiceForm, useWebSocket } from './hooks'
import { InvoiceDetails, InvoiceItems } from './components'
import { InvoiceComms } from '../Comms'
import coreReducer from '../../../../core/utils/reducer'
import CoreEnums from '../../../../core/utils/enums'
import dayjs from 'dayjs'
import CustomRow from '../../../reusable-components/CustomRow'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileInvoiceDollar,
  faArrowLeft,
  faSave,
  faPaperPlane,
  faTrash,
  faShoppingCart
} from '@fortawesome/free-solid-svg-icons'
import AgiliteSkeleton from '../../../reusable-components/AgiliteSkeleton'
import PageHeader from '../../../reusable-components/PageHeader'
import { ContainerCard } from '../../../reusable-components/AgiliteCards'
import './styles/invoice-form.css'

/**
 * Component for creating and editing invoices
 */
const InvoiceForm = ({ data = {}, isNew = false, handleGetInvoices, origin }) => {
  const dispatch = useDispatch()
  const authState = useSelector((state) => state.auth)
  const [form] = Form.useForm()
  const [previousScreen, setPreviousScreen] = useState(null)

  // Detect the source/previous screen
  useEffect(() => {
    // If origin is explicitly passed, use it
    if (origin) {
      setPreviousScreen(origin)
    } else {
      // Check the referrer or document.referrer
      const referrer = document.referrer
      if (referrer.includes('dashboard')) {
        setPreviousScreen('dashboard')
      } else {
        setPreviousScreen('invoices')
      }
    }
  }, [origin])

  // Handle closing the tab and refreshing data
  const handleCloseTab = useCallback(() => {
    if (handleGetInvoices) {
      handleGetInvoices()
    }

    dispatch(
      coreReducer.actions.closeTab({
        targetKey: isNew ? `${CoreEnums.tabKeys.INVOICES}_new` : `${CoreEnums.tabKeys.INVOICES}_${data._id}`,
        removeBreadcrumb: true
      })
    )
  }, [dispatch, handleGetInvoices, isNew, data._id])

  // Handle back button click - simply close the current tab
  const handleBack = useCallback(() => {
    if (handleGetInvoices) {
      handleGetInvoices()
    }

    // Just close the current tab - the framework will handle the navigation back
    dispatch(
      coreReducer.actions.closeTab({
        targetKey: isNew ? `${CoreEnums.tabKeys.INVOICES}_new` : `${CoreEnums.tabKeys.INVOICES}_${data._id}`,
        removeBreadcrumb: true
      })
    )
  }, [dispatch, handleGetInvoices, isNew, data._id])

  // Initialize the invoice form hook
  const {
    loading,
    formLoading,
    invoiceData,
    items,
    bookingRef,
    resubmitEnabled,
    saveForLaterEnabled,
    setItems,
    updateInvoiceFields,
    fetchBillingRecords,
    submitInvoice,
    saveForLater
  } = useInvoiceForm(data, isNew, handleCloseTab)

  // Memoize the websocket message handler
  const handleWebSocketMessage = useCallback(() => {
    message.info('Billing records have been updated by another user. Please Refresh')
  }, [])

  // Initialize the websocket for real-time updates
  const billingWebSocket = useWebSocket(
    'billing',
    data.bookingRef || bookingRef,
    authState.agiliteUser?._id,
    handleWebSocketMessage
  )

  // Handle form field changes
  const handleFieldChange = (changedFields, fields) => {
    const updatedFields = { ...invoiceData, ...changedFields }

    if (updatedFields.bookingDate) {
      updatedFields.bookingDate = dayjs(updatedFields.bookingDate)
    }

    updateInvoiceFields(changedFields)
    form.setFieldsValue(updatedFields)
  }

  // Handle entity selection in the form
  const handleEntitySelect = (entity, type) => {
    switch (type) {
      case 'Patient':
        // When a patient is selected, automatically set the invoice type
        const newInvoiceType = entity.medicalAid?.name ? 'medical' : 'private'
        updateInvoiceFields({
          patientRecord: entity,
          userRef: entity._id,
          invoiceType: newInvoiceType
        })
        // Also update the form fields directly to ensure the UI reflects the change
        form.setFieldsValue({
          userRef: entity._id,
          invoiceType: newInvoiceType
        })
        break
      case 'Practitioner':
        updateInvoiceFields({ medicalProfRecord: entity, medicalProfRef: entity._id })
        form.setFieldValue('medicalProfRef', entity._id)
        break
      case 'Clinic':
        updateInvoiceFields({ clinicRecord: entity, clinicRef: entity._id })
        form.setFieldValue('clinicRef', entity._id)
        break
      default:
        break
    }
  }

  // Handle form submission
  const handleSubmit = async () => {
    try {
      await form.validateFields()
      await submitInvoice(form.getFieldsValue())
    } catch (e) {
      // Validation error, already handled by form
    }
  }

  // Handle saving for later
  const handleSaveForLater = async () => {
    try {
      // Get values directly without validation
      const formValues = form.getFieldsValue()

      // Create a structured object with all required data for saving
      const saveData = {
        ...formValues,
        // Ensure we have the proper IDs and reference data
        userRef: invoiceData.patientRecord?._id,
        medicalProfRef: invoiceData.medicalProfRecord?._id,
        clinicRef: invoiceData.clinicRecord?._id,
        // Include the full records for display purposes
        patientRecord: invoiceData.patientRecord,
        medicalProfRecord: invoiceData.medicalProfRecord,
        clinicRecord: invoiceData.clinicRecord
      }

      await saveForLater(saveData)
    } catch (e) {
      // Error handling is managed by the hook
      console.error('Save for later error:', e)
      message.error('Failed to save invoice. Please try again.')
    }
  }

  const { token } = theme.useToken()

  // Load skeleton UI when form is loading
  const renderLoadingSkeleton = () => {
    return (
      <>
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Skeleton.Avatar size={40} active style={{ marginRight: 8 }} />
              <Skeleton.Input style={{ width: 260, height: 30 }} active />
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 24px' }}>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              {/* Patient & Practitioner Details Card Skeleton */}
              <Card
                bordered
                style={{
                  marginBottom: '16px',
                  borderRadius: '8px',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
                }}
              >
                <Skeleton.Input style={{ width: 240, marginBottom: 16 }} active />

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Skeleton.Input style={{ width: 100, marginBottom: 8 }} active />
                      <Skeleton.Input style={{ width: '100%', height: 32 }} active />
                    </div>

                    <Row gutter={[16, 0]}>
                      <Col span={12}>
                        <div style={{ marginBottom: 16 }}>
                          <Skeleton.Input style={{ width: 100, marginBottom: 8 }} active />
                          <Skeleton.Input style={{ width: '100%', height: 32 }} active />
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ marginBottom: 16 }}>
                          <Skeleton.Input style={{ width: 100, marginBottom: 8 }} active />
                          <Skeleton.Input style={{ width: '100%', height: 32 }} active />
                        </div>
                      </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                      <Col span={12}>
                        <div style={{ marginBottom: 16 }}>
                          <Skeleton.Input style={{ width: 100, marginBottom: 8 }} active />
                          <Skeleton.Input style={{ width: '100%', height: 32 }} active />
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ marginBottom: 16 }}>
                          <Skeleton.Input style={{ width: 100, marginBottom: 8 }} active />
                          <Skeleton.Input style={{ width: '100%', height: 32 }} active />
                        </div>
                      </Col>
                    </Row>
                  </Col>

                  <Col xs={24} md={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Skeleton.Input style={{ width: 100, marginBottom: 8 }} active />
                      <Skeleton.Input style={{ width: '100%', height: 32 }} active />
                    </div>

                    <div style={{ marginBottom: 16 }}>
                      <Skeleton.Input style={{ width: 100, marginBottom: 8 }} active />
                      <Skeleton.Input style={{ width: '100%', height: 32 }} active />
                    </div>

                    <Row gutter={[16, 0]}>
                      <Col span={12}>
                        <div style={{ marginBottom: 16 }}>
                          <Skeleton.Input style={{ width: 100, marginBottom: 8 }} active />
                          <Skeleton.Input style={{ width: '100%', height: 32 }} active />
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ marginBottom: 16 }}>
                          <Skeleton.Input style={{ width: 100, marginBottom: 8 }} active />
                          <Skeleton.Input style={{ width: '100%', height: 32 }} active />
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card>

              {/* Invoice Items Card Skeleton */}
              <Card style={{ marginBottom: '16px', borderRadius: '8px' }} bordered>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FontAwesomeIcon
                        icon={faShoppingCart}
                        style={{ color: token.colorPrimary, marginRight: '8px' }}
                      />
                      <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>Invoice Items</span>
                    </div>
                    <div>
                      <Skeleton.Button style={{ width: 32, marginRight: 8 }} active />
                      <Skeleton.Button style={{ width: 32 }} active />
                    </div>
                  </div>
                </div>

                <Table
                  loading={true}
                  columns={[
                    { title: 'Type', dataIndex: 'type', width: '10%' },
                    { title: 'Code', dataIndex: 'code', width: '10%' },
                    { title: 'Name', dataIndex: 'name' },
                    { title: 'Qty', dataIndex: 'qty', width: '5%' },
                    { title: 'ICD10', dataIndex: 'icd10', width: '10%' },
                    { title: 'Pack Size', dataIndex: 'packSize', width: '5%' },
                    { title: 'Total', dataIndex: 'total', width: '7%' },
                    { title: 'Medical Aid', dataIndex: 'medicalAid', width: '7%' },
                    { title: 'Patient', dataIndex: 'patient', width: '7%' },
                    { title: 'Actions', dataIndex: 'actions', width: '5%' }
                  ]}
                  dataSource={[]}
                  pagination={false}
                />

                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                  <div style={{ width: 300 }}>
                    <Row>
                      <Col span={12}>
                        <strong>Totals</strong>
                      </Col>
                      <Col span={4}>
                        <Skeleton.Input style={{ width: '90%' }} active />
                      </Col>
                      <Col span={4}>
                        <Skeleton.Input style={{ width: '90%' }} active />
                      </Col>
                      <Col span={4}>
                        <Skeleton.Input style={{ width: '90%' }} active />
                      </Col>
                    </Row>
                  </div>
                </div>
              </Card>

              {/* Action Buttons Skeleton */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                <Space size='middle'>
                  <Skeleton.Button style={{ width: 120, height: 40 }} active />
                  <Skeleton.Button style={{ width: 160, height: 40 }} active />
                  <Skeleton.Button style={{ width: 160, height: 40 }} active />
                </Space>
              </div>
            </Col>
          </Row>
        </div>
      </>
    )
  }

  if (formLoading) {
    return renderLoadingSkeleton()
  }

  return (
    <>
      <div style={{ marginTop: 8 }}>
        <PageHeader
          title={isNew ? 'Create New Invoice' : `Invoice: ${invoiceData.invoiceNo}`}
          icon={faFileInvoiceDollar}
        />
      </div>

      <div style={{ padding: '16px 24px' }}>
        <Row gutter={[0, 16]}>
          {/* Form Content */}
          <Col span={24}>
            <Form
              form={form}
              layout='vertical'
              initialValues={
                !formLoading
                  ? {
                      userRef: invoiceData.userRef
                        ? `${invoiceData.patientRecord?.firstName} ${invoiceData.patientRecord?.lastName} (${invoiceData.patientRecord?.idNo})`
                        : undefined,
                      medicalProfRef: invoiceData.medicalProfRef
                        ? `${invoiceData.medicalProfRecord?.firstName} ${invoiceData.medicalProfRecord?.lastName}`
                        : undefined,
                      clinicRef: invoiceData.clinicRef ? invoiceData.clinicRecord?.name : undefined,
                      bookingDate: invoiceData.bookingDate
                        ? dayjs(invoiceData.bookingDate)
                        : invoiceData.bookingRecord?.bookingDate
                        ? dayjs(invoiceData.bookingRecord.bookingDate)
                        : undefined,
                      invoiceType: invoiceData.invoiceType,
                      authNo: invoiceData.authNo,
                      refMedProfRef: invoiceData.refMedProfRef
                        ? `${invoiceData.refMedProfRecord?.firstName} ${invoiceData.refMedProfRecord?.lastName}`
                        : undefined,
                      assMedProfRef: invoiceData.assMedProfRef
                        ? `${invoiceData.assMedProfRecord?.firstName} ${invoiceData.assMedProfRecord?.lastName}`
                        : undefined
                    }
                  : {}
              }
              onFieldsChange={handleFieldChange}
            >
              {/* Invoice Details Section */}
              <InvoiceDetails
                isNew={isNew}
                saveForLaterEnabled={saveForLaterEnabled}
                onEntitySelect={handleEntitySelect}
                invoiceData={invoiceData}
              />

              {/* Invoice Items Section */}
              <Card className='invoice-card' style={{ marginTop: '16px', marginBottom: '16px' }} bordered>
                <InvoiceItems
                  items={items}
                  setItems={setItems}
                  bookingRef={bookingRef}
                  patientRecord={invoiceData.patientRecord}
                  loading={loading}
                  onRefresh={fetchBillingRecords}
                  billingWebSocket={billingWebSocket}
                  selectedInvoiceType={form.getFieldValue('invoiceType')}
                />
              </Card>

              {/* Communications & Notes Section */}
              <InvoiceComms invoiceRef={data._id} invoiceData={invoiceData} disabled={isNew && !data._id} />

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                <Space size='middle'>
                  {isNew || saveForLaterEnabled ? (
                    <>
                      <Popconfirm
                        title='Are you sure you want to discard this invoice?'
                        onConfirm={handleCloseTab}
                        okText='Yes'
                        cancelText='No'
                      >
                        <Button
                          danger
                          icon={<FontAwesomeIcon icon={faTrash} />}
                          style={{
                            minWidth: '120px',
                            height: '40px'
                          }}
                        >
                          Discard
                        </Button>
                      </Popconfirm>

                      {saveForLaterEnabled && (
                        <Button
                          icon={<FontAwesomeIcon icon={faSave} />}
                          style={{
                            minWidth: '160px',
                            height: '40px'
                          }}
                          onClick={handleSaveForLater}
                          loading={loading}
                        >
                          Save for Later
                        </Button>
                      )}

                      <Button
                        type='primary'
                        icon={<FontAwesomeIcon icon={faPaperPlane} />}
                        style={{
                          minWidth: '160px',
                          height: '40px'
                        }}
                        onClick={handleSubmit}
                        loading={loading}
                      >
                        Submit Invoice
                      </Button>
                    </>
                  ) : resubmitEnabled ? (
                    <Button
                      type='primary'
                      icon={<FontAwesomeIcon icon={faPaperPlane} />}
                      style={{
                        minWidth: '160px',
                        height: '40px'
                      }}
                      onClick={handleSubmit}
                      loading={loading}
                    >
                      Resubmit Invoice
                    </Button>
                  ) : null}
                </Space>
              </div>
            </Form>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default InvoiceForm
